// AiStudyAccessView — admin libera/revoga acesso à feature "Estudando com IA".
// Acesso é por usuário (email). Backend valida que email existe + cria/desrevoga
// linha em ai_study_access.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Sparkles,
  Trash2,
  UserPlus
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { api, ApiError, type AiStudyAccessRow } from "./api";
import { openConfirm } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const phase = signal<"loading" | "ready" | "error">("loading");
const rows = signal<AiStudyAccessRow[]>([]);
const errMsg = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listAiStudyAccess();
    rows.value = r.accesses;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function AiStudyAccessView() {
  useEffect(() => {
    void load();
  }, []);
  const list = rows.value;
  const active = list.filter((r) => r.active);
  const revoked = list.filter((r) => !r.active);

  if (phase.value === "loading") {
    return (
      <div class="space-y-2">
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-24 w-full" />
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
        <Sparkles className="mr-1 inline size-4 text-violet-600" />
        <strong>Piloto fechado.</strong> Esta feature consome mais IA e armazenamento.
        Libere um usuário por vez. A configuração padrão da IA (em Modo Estudo)
        continua disponível pra todos.
      </div>

      <GrantForm />

      <section>
        <h2 class="mb-2 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Acessos ativos ({active.length})
        </h2>
        {active.length === 0 ? (
          <div class="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            Ninguém com acesso ativo no momento.
          </div>
        ) : (
          <ul class="divide-y divide-border overflow-hidden rounded-md border border-border bg-card/40">
            {active.map((r) => (
              <AccessRow key={r.userId} row={r} />
            ))}
          </ul>
        )}
      </section>

      {revoked.length > 0 && (
        <section>
          <h2 class="mb-2 text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Histórico revogado ({revoked.length})
          </h2>
          <ul class="divide-y divide-border overflow-hidden rounded-md border border-dashed border-border bg-card/30 opacity-70">
            {revoked.map((r) => (
              <AccessRow key={r.userId} row={r} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function GrantForm() {
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!email.trim()) return;
    setBusy(true);
    setErr(null);
    setOkMsg(null);
    try {
      const r = await api.grantAiStudyAccess(email.trim().toLowerCase(), notes || undefined);
      setOkMsg(`Acesso liberado pra ${r.email}.`);
      setEmail("");
      setNotes("");
      await load();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="rounded-md border border-border bg-card/40 p-4">
      <h3 class="mb-2 text-sm font-bold">Liberar acesso por email</h3>
      <div class="flex flex-col gap-2 sm:flex-row">
        <input
          type="email"
          value={email}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          placeholder="usuario@example.com"
          class="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
        <input
          type="text"
          value={notes}
          onInput={(e) => setNotes((e.target as HTMLInputElement).value)}
          placeholder="Anotação (opcional)"
          maxLength={500}
          class="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || !email.trim()}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
          Liberar
        </button>
      </div>
      {okMsg && (
        <p class="mt-2 inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="size-3" /> {okMsg}
        </p>
      )}
      {err && (
        <p class="mt-2 text-xs text-destructive">
          <AlertCircle className="mr-1 inline size-3" /> {err}
        </p>
      )}
    </div>
  );
}

function AccessRow({ row }: { row: AiStudyAccessRow }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function revoke() {
    const ok = await openConfirm({
      title: `Revogar acesso de ${row.email}?`,
      message: "O usuário perde acesso ao Estudando com IA imediatamente. Sessões existentes ficam, mas ele não pode abrir novas nem mandar mensagens.",
      confirmLabel: "Revogar acesso",
      danger: true
    });
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      await api.revokeAiStudyAccess(row.userId);
      await load();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li class="flex items-center gap-3 px-4 py-3">
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2 text-sm font-bold">
          {row.name || row.email}
          {row.active ? (
            <span class="rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
              Ativo
            </span>
          ) : (
            <span class="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">
              Revogado
            </span>
          )}
        </div>
        <div class="text-xs text-muted-foreground">{row.email}</div>
        <div class="text-[10px] text-muted-foreground">
          Liberado em {new Date(row.grantedAt).toLocaleString("pt-BR")}
          {row.revokedAt &&
            ` · revogado em ${new Date(row.revokedAt).toLocaleString("pt-BR")}`}
          {row.notes && ` · ${row.notes}`}
        </div>
        {err && <p class="mt-1 text-[10px] text-destructive">{err}</p>}
      </div>
      {row.active && (
        <button
          type="button"
          onClick={() => void revoke()}
          disabled={busy}
          class="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-[11px] font-semibold text-destructive hover:bg-destructive/20 disabled:opacity-50"
          title="Revogar acesso"
        >
          {busy ? <Loader2 className="size-3 animate-spin" /> : <Trash2 className="size-3" />}
          Revogar
        </button>
      )}
    </li>
  );
}
